import { Patient, Practitioner } from '@medplum/fhirtypes';
import dayjs from 'dayjs';

/** The prototype's manual entry choices (BAR-3031). */
export const MANUAL_METHODS = ['Check', 'Bank transfer', 'Cash'] as const;
export const MANUAL_BANK_ACCOUNTS = ['Mercury', 'Rippling'] as const;

/** The `finance-proof` bot's limit. */
export const MAX_PROOF_BYTES = 4 * 1024 * 1024;

export interface ManualEntryDraft {
  direction: 'payment' | 'refund';
  method: (typeof MANUAL_METHODS)[number];
  bankAccount: (typeof MANUAL_BANK_ACCOUNTS)[number];
  /** `YYYY-MM-DD`, today or earlier. */
  date: string;
  amount: string;
  patient?: Patient;
  proof?: File;
  note: string;
}

export function emptyDraft(now: Date): ManualEntryDraft {
  return {
    direction: 'payment',
    method: 'Check',
    bankAccount: 'Mercury',
    date: dayjs(now).format('YYYY-MM-DD'),
    amount: '',
    note: '',
  };
}

/** The amount typed, in dollars; 0 when it isn't a number. */
export function draftAmount(draft: ManualEntryDraft): number {
  const amount = Number.parseFloat(draft.amount.replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : 0;
}

/** Add payment / Add refund stays disabled without an amount, a patient and a proof. */
export function canSubmit(draft: ManualEntryDraft, now: Date): boolean {
  return (
    draftAmount(draft) > 0 &&
    Boolean(draft.patient) &&
    Boolean(draft.proof) &&
    !dayjs(draft.date).isAfter(dayjs(now), 'day')
  );
}

export function submitLabel(draft: ManualEntryDraft): string {
  return draft.direction === 'payment' ? 'Add payment' : 'Add refund';
}

/** The bank's date, as the instant of local midnight, so the ledger shows the date alone. */
export function occurredAt(draft: ManualEntryDraft): string {
  return dayjs(draft.date).startOf('day').toISOString();
}

/** `Priya S.`, as the prototype's history writes who imported. */
export function shortName(practitioner: Practitioner | undefined): string {
  const name = practitioner?.name?.[0];
  const given = name?.given?.[0] ?? '';
  const family = name?.family ?? '';
  return [given, family ? `${family[0]}.` : ''].filter(Boolean).join(' ');
}

/** `184 KB`. */
export function formatFileSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** The file's bytes as base64, for the `finance-proof` bot's input. */
export function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // A data URL is `data:<type>;base64,<data>`.
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error(`Couldn't read ${file.name}`));
    reader.readAsDataURL(file);
  });
}
